import { onPageIn, onMount, onDestroy } from '@/modules/_'

interface WorkFilterConfig {
  readonly filterButtons: NodeListOf<HTMLElement>
  readonly workCards: NodeListOf<HTMLElement>
  readonly container: HTMLElement
  activeFilter: string
}

interface FilterState {
  readonly isActive: boolean
  readonly categories: ReadonlyArray<string>
}

interface WorkCardData {
  readonly element: HTMLElement
  readonly categories: ReadonlyArray<string>
  readonly isVisible: boolean
}

type FilterVariant = 'filled' | 'default'
type AnimationDuration = number

const ANIMATION_DURATION: AnimationDuration = 300
const FILTER_ALL_VALUE: string = 'all'

export default function (element: HTMLElement, dataset: DOMStringMap): void {
  let filterConfig: WorkFilterConfig | null = null
  let eventCleanupFunctions: Array<() => void> = []

  function parseCategories(
    categoryAttribute: string | null
  ): ReadonlyArray<string> {
    if (!categoryAttribute) return []

    return categoryAttribute
      .split(',')
      .map((category: string) => category.trim().toLowerCase())
      .filter((category: string) => category.length > 0)
  }

  function getWorkCardData(card: HTMLElement): WorkCardData {
    const categoryAttr: string | null = card.getAttribute('data-category')
    const categories: ReadonlyArray<string> = parseCategories(categoryAttr)

    return {
      element: card,
      categories,
      isVisible: true,
    }
  }

  function getAllWorkCards(): ReadonlyArray<WorkCardData> {
    if (!filterConfig) return []

    return Array.from(filterConfig.workCards).map((card: HTMLElement) =>
      getWorkCardData(card)
    )
  }

  function getFilterState(filterButton: HTMLElement): FilterState {
    const isActive: boolean = filterButton.classList.contains(
      'w-variant-cbb8053d-e819-d36b-ca78-bb52aba3f1fc'
    )

    const filterValue: string =
      filterButton.textContent?.trim().toLowerCase() || ''
    const categories: ReadonlyArray<string> =
      filterValue === 'all' ? [] : [filterValue]

    return {
      isActive,
      categories,
    }
  }

  function updateFilterButtonState(
    button: HTMLElement,
    isActive: boolean
  ): void {
    if (isActive) {
      button.classList.add('w-variant-cbb8053d-e819-d36b-ca78-bb52aba3f1fc')
    } else {
      button.classList.remove('w-variant-cbb8053d-e819-d36b-ca78-bb52aba3f1fc')
    }
  }

  function shouldShowCard(
    cardData: WorkCardData,
    activeCategories: ReadonlyArray<string>
  ): boolean {
    // Show all if no filter is active or 'all' is selected
    if (activeCategories.length === 0) return true

    // Check if card has any of the active categories
    return activeCategories.some((activeCategory: string) =>
      cardData.categories.includes(activeCategory)
    )
  }

  function animateCardVisibility(
    card: HTMLElement,
    shouldShow: boolean
  ): Promise<void> {
    return new Promise<void>((resolve: (value: void) => void) => {
      if (shouldShow) {
        // Show card
        card.style.display = 'block'
        card.style.opacity = '0'
        card.style.transform = 'translateY(20px)'
        card.style.transition = `all ${ANIMATION_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`

        // Force reflow
        void card.offsetHeight

        requestAnimationFrame((): void => {
          card.style.opacity = '1'
          card.style.transform = 'translateY(0)'

          setTimeout(() => resolve(), ANIMATION_DURATION)
        })
      } else {
        // Hide card
        card.style.opacity = '0'
        card.style.transform = 'translateY(-20px)'
        card.style.transition = `all ${ANIMATION_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`

        setTimeout((): void => {
          card.style.display = 'none'
          resolve()
        }, ANIMATION_DURATION)
      }
    })
  }

  async function filterWorkCards(
    activeCategories: ReadonlyArray<string>
  ): Promise<void> {
    if (!filterConfig) return

    const workCards: ReadonlyArray<WorkCardData> = getAllWorkCards()
    const animationPromises: Array<Promise<void>> = []

    workCards.forEach((cardData: WorkCardData) => {
      const shouldShow: boolean = shouldShowCard(cardData, activeCategories)
      const animationPromise: Promise<void> = animateCardVisibility(
        cardData.element,
        shouldShow
      )
      animationPromises.push(animationPromise)
    })

    // Wait for all animations to complete
    await Promise.all(animationPromises)

    // Trigger layout recalculation if needed
    if (filterConfig.container) {
      filterConfig.container.dispatchEvent(
        new CustomEvent('filter-complete', {
          detail: {
            activeCategories,
            visibleCount: workCards.filter((card) =>
              shouldShowCard(card, activeCategories)
            ).length,
          },
        })
      )
    }
  }

  function handleFilterClick(
    filterButton: HTMLElement
  ): (event: MouseEvent) => Promise<void> {
    return async function (event: MouseEvent): Promise<void> {
      event.preventDefault()

      if (!filterConfig) return

      const filterText: string =
        filterButton.textContent?.trim().toLowerCase() || ''
      const filterCategories: ReadonlyArray<string> =
        filterText === FILTER_ALL_VALUE ? [] : [filterText]

      // Update button states
      filterConfig.filterButtons.forEach((button: HTMLElement) => {
        const isThisButton: boolean = button === filterButton
        updateFilterButtonState(button, isThisButton)
      })

      // Update active filter
      filterConfig.activeFilter = filterText

      // Apply filter with animation
      await filterWorkCards(filterCategories)
    }
  }

  function getFilterValueFromButton(button: HTMLElement): string {
    // Extract filter value from button text content
    const textContent: string = button.textContent?.trim() || ''

    // Handle special cases for button text vs filter values
    const filterMap: Record<string, string> = {
      featured: 'featured',
      brand: 'brand',
      marketing: 'marketing',
      product: 'product',
      all: FILTER_ALL_VALUE,
    }

    return filterMap[textContent.toLowerCase()] || textContent.toLowerCase()
  }

  function initializeFilters(): void {
    const filterButtons: NodeListOf<HTMLElement> =
      document.querySelectorAll('.work_filter')
    const workCards: NodeListOf<HTMLElement> =
      document.querySelectorAll('[data-category]')
    const container: HTMLElement | null =
      document.querySelector('.work_header_wrap')

    if (!filterButtons.length || !workCards.length) {
      console.warn('Work filter: Filter buttons or work cards not found')
      return
    }

    filterConfig = {
      filterButtons,
      workCards,
      container: container || element,
      activeFilter: FILTER_ALL_VALUE,
    }

    // Find featured button or use first button as default
    const featuredButton: HTMLElement | null =
      Array.from(filterButtons).find(
        (button: HTMLElement) =>
          button.textContent?.trim().toLowerCase() === 'featured'
      ) || null

    const defaultButton: HTMLElement = featuredButton || filterButtons[0]

    // Set up initial states and event handlers
    filterButtons.forEach((button: HTMLElement) => {
      const isDefault: boolean = button === defaultButton
      updateFilterButtonState(button, isDefault)

      // Debug: Log which button is getting the active class
      if (isDefault) {
        console.log(
          'Setting active class on button:',
          button.textContent?.trim(),
          button.classList.contains(
            'w-variant-cbb8053d-e819-d36b-ca78-bb52aba3f1fc'
          )
        )
      }

      // Set up click handlers
      const clickHandler = handleFilterClick(button)
      button.addEventListener('click', clickHandler)

      eventCleanupFunctions.push((): void => {
        button.removeEventListener('click', clickHandler)
      })
    })

    // Apply initial filter - the default button should already have the active class applied
    if (featuredButton) {
      filterConfig.activeFilter = 'featured'
      void filterWorkCards(['featured'])
    }
  }

  function cleanup(): void {
    // Clean up all event listeners
    eventCleanupFunctions.forEach((cleanupFn: () => void): void => {
      cleanupFn()
    })
    eventCleanupFunctions = []

    // Reset all cards to visible
    if (filterConfig) {
      filterConfig.workCards.forEach((card: HTMLElement) => {
        card.style.display = 'block'
        card.style.opacity = '1'
        card.style.transform = 'translateY(0)'
        card.style.transition = ''
      })
    }

    filterConfig = null
  }

  // Module lifecycle hooks
  onMount(() => {
    initializeFilters()
  })

  onDestroy(() => {
    cleanup()
  })
}
